import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { Box } from "@mui/material";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useElementSize } from "../../hooks/index.js";
import { ErrorFallback } from "./components/ErrorFallback.js";
import { Loader } from "./components/Loader.js";
import { LoadingErrorAlert } from "./components/LoadingErrorAlert.js";
import { Placeholder } from "./components/Placeholder.js";
const defaultSandbox = [
  "allow-popups",
  "allow-scripts",
  "allow-same-origin",
  "allow-popups-to-escape-sandbox"
];
const defaultPostMessagesMode = ["size"];
const IFrame = forwardRef(function IFrame2(props, ref) {
  const {
    url,
    name = "tidal-iframe",
    style = {},
    allow,
    testId = "iframe-root",
    sandbox = defaultSandbox,
    loading = "lazy",
    loadingErrorText,
    errorFallback,
    referrerPolicy = "no-referrer",
    postMessagesMode = defaultPostMessagesMode,
    disabled,
    disabledPlaceholder,
    disabledPrefetch
  } = props;
  const rIFrame = useRef(null);
  const rContainer = useRef(null);
  const size = useElementSize(rContainer.current);
  const { width, height, resizing } = size;
  const rSize = useRef(size);
  useLayoutEffect(() => void (rSize.current = size), [size]);
  const rPostMessages = useRef(postMessagesMode);
  useLayoutEffect(() => void (rPostMessages.current = postMessagesMode), [postMessagesMode]);
  const sandboxAsString = useMemo(() => sandbox.join(" "), [sandbox]);
  useImperativeHandle(
    ref,
    () => ({
      notify: (msg) => {
        rIFrame.current?.contentWindow?.postMessage(msg, "*");
      }
    }),
    []
  );
  const [iframeUrl, setIframeUrl] = useState("");
  const [status, setStatus] = useState("loading");
  const notifyResize = useCallback((width2, height2, resizing2) => {
    if (rPostMessages.current.includes("size")) {
      const resizeMessage = {
        type: "resize",
        source: "workwave",
        width: width2,
        height: height2,
        resizing: resizing2
      };
      rIFrame.current?.contentWindow?.postMessage(resizeMessage, "*");
    }
  }, []);
  useEffect(() => {
    if (disabledPrefetch) {
      setIframeUrl(url);
      setStatus("ready");
      return;
    }
    if (disabled) return;
    let effectCleared = false;
    const controller = new AbortController();
    const { signal } = controller;
    setStatus("loading");
    try {
      fetch(url, { signal, mode: "no-cors" }).then(() => {
        if (effectCleared) return;
        setIframeUrl(url);
        setStatus("ready");
      }).catch(() => {
        if (effectCleared) return;
        setStatus("error");
      });
    } catch (e) {
      if (effectCleared) return;
      setStatus("error");
    }
    return () => {
      effectCleared = true;
      controller.abort();
    };
  }, [url, disabled, disabledPrefetch]);
  useEffect(() => {
    postMessagesMode;
    if (status === "ready") {
      notifyResize(width, height, resizing);
    }
  }, [notifyResize, postMessagesMode, status, width, height, resizing]);
  useEffect(() => {
    if (status === "ready") {
      let handleIFrameMessage2 = function(event) {
        if (event.data.type === "refreshInfo") {
          const { width: width2, height: height2, resizing: resizing2 } = rSize.current;
          notifyResize(width2, height2, resizing2);
        }
      };
      var handleIFrameMessage = handleIFrameMessage2;
      window.addEventListener("message", handleIFrameMessage2);
      return () => {
        window.removeEventListener("message", handleIFrameMessage2);
      };
    }
  }, [notifyResize, status]);
  const iFrameVisibility = iframeUrl.length === 0 || status !== "ready" ? "hidden" : "visible";
  const enabled = !disabled;
  return /* @__PURE__ */ jsx(
    Box,
    {
      ref: rContainer,
      "data-testid": testId,
      sx: { width: "100%", height: "100%", position: "relative", top: 0, left: 0 },
      "data-tidal": "IFrame",
      children: /* @__PURE__ */ jsxs(
        Box,
        {
          sx: {
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            position: "absolute"
          },
          children: [
            disabled && /* @__PURE__ */ jsx(Placeholder, { children: disabledPlaceholder }),
            enabled && /* @__PURE__ */ jsxs(Fragment, { children: [
              status === "error" && errorFallback && /* @__PURE__ */ jsx(ErrorFallback, { children: errorFallback }),
              status === "error" && loadingErrorText && /* @__PURE__ */ jsx(LoadingErrorAlert, { loadingErrorText }),
              status === "loading" && /* @__PURE__ */ jsx(Loader, {}),
              /* @__PURE__ */ jsx(
                "iframe",
                {
                  title: "tidal iFrame",
                  src: iframeUrl,
                  ref: rIFrame,
                  name,
                  allow,
                  width: `${width}px`,
                  height: `${height}px`,
                  loading,
                  sandbox: sandboxAsString,
                  referrerPolicy,
                  allowFullScreen: false,
                  style: {
                    border: 0,
                    padding: 0,
                    margin: 0,
                    ...style,
                    visibility: iFrameVisibility,
                    zIndex: 0
                  }
                }
              )
            ] })
          ]
        }
      )
    }
  );
});
export {
  IFrame
};
//# sourceMappingURL=IFrame.js.map
